import { useState } from "react";
import { FiCheck, FiEdit2 } from "react-icons/fi";
import { cn } from "utils/helpers";
import { useProcurementMethodNamesSelected } from "newui/store/utils";
import { ModalNew as Modal } from "components/Modal/ModalNew";
import { SmartFieldProjectEditor } from "components/RFQ/SmartFieldEditor/SmartFieldProjectEditor";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";

interface ProjectExperienceItem {
	id: number;
	name?: string;
	client_name?: string;
	procurementMethodIds?: number[];
	value?: number;
	year_completed?: number;
}

interface ProjectExperienceTableViewProps {
	companyId?: number;
	isPreview?: boolean;
	isSubmitting?: boolean;
	items: ProjectExperienceItem[];
	selectedIds: number[];
	onSelect: (id: number) => void;
	onEdit?: (id: number) => void;
}

const formatCurrency = (value?: number): string => {
	if (!value) {
		return "-";
	}
	if (value >= 1000000) {
		return `$${(value / 1000000).toFixed(1)}M`;
	}
	if (value >= 1000) {
		return `$${(value / 1000).toFixed(0)}K`;
	}
	return `$${value}`;
};

const ProcurementModelCell: React.FC<{ ids?: number[] }> = ({ ids }) => {
	const { selectedValuesCsv } = useProcurementMethodNamesSelected(ids);
	return <>{selectedValuesCsv || "-"}</>;
};

export const ProjectExperienceTableView: React.FC<ProjectExperienceTableViewProps> = ({
	companyId,
	isPreview,
	isSubmitting,
	items,
	onEdit,
	onSelect,
	selectedIds,
}) => {
	const [editingItemId, setEditingItemId] = useState<number | null>(null);
	const { rfxId } = useRfxSubmissionData();
	const isSelected = (id: number): boolean => selectedIds.includes(id);

	const handleEdit = (id: number): void => {
		if (onEdit) {
			onEdit(id);
		} else {
			setEditingItemId(id);
		}
	};

	const editingItem = editingItemId !== null ? items.find((i) => i.id === editingItemId) : null;

	return (
		<div className="w-full overflow-x-auto">
			<table className="w-full min-w-[700px] border-collapse text-sm">
				<thead>
					<tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
						<th className="px-3 py-3">Name</th>
						<th className="px-3 py-3">Construction Value</th>
						<th className="px-3 py-3">Completion</th>
						<th className="px-3 py-3">Client Name</th>
						<th className="px-3 py-3">Procurement Model</th>
						<th className="px-3 py-3 text-right">Actions</th>
					</tr>
				</thead>
				<tbody>
					{items.map((item) => {
						const selected = isSelected(item.id);
						return (
							<tr
								key={item.id}
								onClick={() => !isPreview && onSelect(item.id)}
								className={cn(
									"border-b border-gray-100 transition-colors",
									selected
										? "bg-blue-50"
										: "hover:bg-gray-50",
									!isPreview && "cursor-pointer"
								)}
							>
								<td className="px-3 py-3 font-medium text-gray-900">
									{item.name || "-"}
								</td>
								<td className="px-3 py-3 text-gray-600">
									{formatCurrency(item.value)}
								</td>
								<td className="px-3 py-3 text-gray-600">
									{item.year_completed || "-"}
								</td>
								<td className="px-3 py-3 text-gray-600">
									{item.client_name || "-"}
								</td>
								<td className="px-3 py-3 text-gray-600">
									<ProcurementModelCell ids={item.procurementMethodIds} />
								</td>
								<td className="px-3 py-3">
									<div className="flex items-center justify-end gap-2">
										{!isPreview && (
											<button
												type="button"
												onClick={() => onSelect(item.id)}
												disabled={isSubmitting}
												className={cn(
													"rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50",
													selected
														? "border border-blue-500 bg-blue-500 text-white"
														: "border border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-500"
												)}
											>
												{selected ? (
													<span className="flex items-center gap-1">
														<FiCheck size={12} />
														Selected
													</span>
												) : (
													"Select"
												)}
											</button>
										)}
										<button
											type="button"
											onClick={(e) => { e.stopPropagation(); handleEdit(item.id); }}
											className="rounded-md border border-gray-300 p-1.5 text-gray-500 transition-colors hover:border-blue-400 hover:text-blue-500"
											aria-label="Edit"
										>
											<FiEdit2 size={14} />
										</button>
									</div>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			{editingItemId !== null && (
				<Modal
					isOpen={editingItemId !== null}
					onClose={() => setEditingItemId(null)}
					headerContent={`Edit ${editingItem?.name ?? "Project"}`}
				>
					<SmartFieldProjectEditor
						projectId={editingItemId}
						rfxId={rfxId}
						companyId={companyId}
						onRequestClose={() => setEditingItemId(null)}
					/>
				</Modal>
			)}
		</div>
	);
};
